import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { KeywordInput } from '@/lib/types';

export const keywordKeys = {
  all: (jobId: string) => ['jobs', jobId, 'keywords'] as const,
  list: (jobId: string) => ['jobs', jobId, 'keywords', 'list'] as const,
  activity: (jobId: string, params: { keywordId?: string; page?: number; pageSize?: number }) => ['jobs', jobId, 'keywords', 'activity', params] as const,
  qr: (jobId: string, id: string, size: number) => ['jobs', jobId, 'keywords', 'qr', id, size] as const,
};

export function useKeywords(jobId: string) {
  return useQuery({ queryKey: keywordKeys.list(jobId), queryFn: () => api.keywords.list(jobId) });
}

export function useKeywordActivity(jobId: string, params: { keywordId?: string; page?: number; pageSize?: number }) {
  return useQuery({ queryKey: keywordKeys.activity(jobId, params), queryFn: () => api.keywords.activity(jobId, params), placeholderData: keepPreviousData, refetchInterval: 15_000 });
}

export function useKeywordQr(jobId: string, id: string | null, size = 512) {
  return useQuery({ queryKey: keywordKeys.qr(jobId, id ?? '', size), queryFn: () => api.keywords.qrSvg(jobId, id as string, size), enabled: !!id, staleTime: 5 * 60_000 });
}

export function useKeywordMutations(jobId: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: keywordKeys.all(jobId) });
  const create = useMutation({ mutationFn: (body: KeywordInput) => api.keywords.create(jobId, body), onSuccess: () => invalidate() });
  const update = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<KeywordInput> }) => api.keywords.update(jobId, id, body), onSuccess: () => invalidate() });
  const remove = useMutation({ mutationFn: (id: string) => api.keywords.remove(jobId, id), onSuccess: () => invalidate() });
  const test = useMutation({ mutationFn: ({ body, language }: { body: string; language?: string }) => api.keywords.test(jobId, body, language) });
  return { create, update, remove, test };
}
